import Cookies from 'js-cookie';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Utils } from '../lib/utils';
import { DeviceConfiguration, notAcceptedValues } from './device';
import loadRelevantId from '../services/load-rvid';

export type RelevantIdResponse =
  | string
  | {
      deviceId: string;
      relevantId: Record<string, any>;
    };

export class RelevantId {
  private _deviceConfig: DeviceConfiguration;

  constructor(deviceConfig: DeviceConfiguration) {
    this._deviceConfig = deviceConfig;
  }

  /**
   * Call NEML Device API to process a relevantId response payload.
   * @param relevantId - Imperium response payload.
   * @private
   */
  private async _callNemlApi(relevantId: any): Promise<string> {
    const options: AxiosRequestConfig = {
      method: 'POST',
      headers: { 'content-type': 'text/plain' },
      data: JSON.stringify({
        onlineId: this._deviceConfig.onlineId,
        profile: this._deviceConfig.profile,
        profileId: this._deviceConfig.profileId,
        relevantId,
      }),
      url: this._deviceConfig.apiRvIdURL,
    };

    try {
      const response: AxiosResponse = await axios.request(options);
      return Utils.getValue(() => response.data.deviceId, undefined);
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  /**
   * Call RelevantID API.
   * @private
   */
  /* istanbul ignore next */
  private _callRelevantIDApi(): Promise<any> {
    const SurveyID = Utils.getValue(process.env.RVID_SURVEYID, '99999');
    const rvIDObj = {
      ClientID: process.env.RVID_CLIENTID,
      SurveyID,
    };

    const timeout = setTimeout(() => {
      console.log('RelevantID timed out after 10 seconds');
    }, 10000);

    return new Promise((resolve, reject) => {
      Utils.getWindow().relevantID.callService(rvIDObj, function callback(responseData) {
        if (responseData.ErrorCode === 1) {
          const error = 'Error fetching RVid';
          console.error(error);
          reject(new Error(error));
        } else {
          resolve(responseData);
        }
        clearTimeout(timeout);
      });
    });
  }

  /**
   * Get RelevantID from cache.
   * @private
   */
  private async _getRelevantIDFromCache(): Promise<RelevantIdResponse> {
    if (this._deviceConfig.version === 1 && this._deviceConfig.useCache) {
      const cookieRelevantId = await Cookies.get('rv-id');
      if (!Utils.isEmpty(cookieRelevantId)) {
        return cookieRelevantId;
      }
    }

    if (this._deviceConfig.version === 2 && this._deviceConfig.useCache) {
      const localStorageRelevantId = localStorage.getItem('rvid');
      const relevantIdParsed = Utils.parseJSON(localStorageRelevantId);
      const deviceId = Utils.getValue(() => relevantIdParsed.deviceId, undefined);
      const relevantId = Utils.getValue(() => relevantIdParsed.relevantId, undefined);

      if (!Utils.isEmpty(deviceId) && !Utils.isEmpty(relevantId)) {
        return { deviceId, relevantId };
      }
    }

    return null;
  }

  /**
   * Load RelevantID script from CDN using proxy.
   * @private
   */
  /* istanbul ignore next */
  private async _loadRelevantIDScript(): Promise<void> {
    if (!this._deviceConfig.rvidURL) {
      this._deviceConfig.rvidURL = '/prx/rvid';
    }

    await loadRelevantId(`${this._deviceConfig.rvidURL}/RelevantID4.js`);
  }

  /**
   * Get a RelevantID.
   */
  public async getRelevantId(): Promise<RelevantIdResponse> {
    // Return relevantId from cache (if any).
    const relevantIdCache = await this._getRelevantIDFromCache();
    if (relevantIdCache) {
      return relevantIdCache;
    }

    // Load relevantId script.
    await this._loadRelevantIDScript();

    // Call relevantId API.
    const relevantId = await this._callRelevantIDApi();

    // Call NEML API & don't wait for result.
    this._callNemlApi(relevantId);

    return Number(this._deviceConfig.version) === 2
      ? {
          deviceId: Utils.getValue(() => relevantId.RVid, undefined),
          relevantId: Utils.getValue(() => relevantId, undefined),
        }
      : Utils.getValue(() => relevantId.RVid, undefined);
  }

  /**
   * Save RelevantID in cache.
   * @param relevantId
   */
  public static saveRelevantToCache(relevantId: RelevantIdResponse): void {
    if (typeof relevantId === 'string' && !Utils.isEmpty(relevantId) && !notAcceptedValues.includes(relevantId)) {
      Cookies.set('rv-id', relevantId, {
        sameSite: 'none',
        secure: true,
        expires: 360,
      });
    }

    if (
      typeof relevantId === 'object' &&
      !Utils.isEmpty(relevantId) &&
      !Utils.isEmpty(relevantId.deviceId) &&
      !notAcceptedValues.includes(relevantId.deviceId)
    ) {
      localStorage.setItem('rvid', JSON.stringify(relevantId));
    }
  }
}
